<template>
  <div class="searchable-country-select position-relative" ref="selectContainer">
    <!-- Selected Country Trigger Box -->
    <div 
      class="cp-input d-flex align-items-center justify-content-between cursor-pointer user-select-none"
      :class="{ 'border-pink-glow': isOpen }"
      @click="toggleDropdown">
      <span v-if="selectedCountry" class="text-white font-size-14 text-truncate">
        {{ selectedCountry.name }}
      </span>
      <span v-else class="text-white-50 font-size-14">
        {{ placeholder || 'Select Country' }}
      </span>
      <i class="fa font-size-12 text-white-50 transition-transform" 
         :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </div>

    <!-- Dropdown Menu -->
    <transition :name="openUpwards ? 'dropdown-fade-up' : 'dropdown-fade'">
      <div 
        v-if="isOpen" 
        class="country-dropdown-menu"
        :class="{ 'open-upwards': openUpwards }">
        <!-- Search Field -->
        <div class="p-2 border-bottom border-white-10 sticky-top" style="background: #18181c; border-radius: 12px 12px 0 0;">
          <div class="search-input-wrapper d-flex align-items-center">
            <i class="fa fa-search text-white-50 font-size-13 me-2 flex-shrink-0"></i>
            <input 
              ref="searchInput"
              type="text" 
              class="country-search-input flex-grow-1" 
              placeholder="Search country..."
              v-model="searchQuery"
              @click.stop>
            <i v-if="searchQuery" 
               class="fa fa-times text-white-50 cursor-pointer font-size-13 ms-2 flex-shrink-0" 
               @click.stop="searchQuery = ''"></i>
          </div>
        </div>

        <!-- Country Options List -->
        <div class="country-options-list" :style="optionsListStyle">
          <div 
            v-for="country in filteredCountries" 
            :key="country.code"
            class="country-option-item d-flex align-items-center justify-content-between"
            :class="{ 'active': country.code === value }"
            @click="selectCountry(country)">
            <span class="country-name">{{ country.name }}</span>
            <i v-if="country.code === value" class="fa fa-check font-size-12" style="color: #70D5C2;"></i>
          </div>

          <div v-if="filteredCountries.length === 0" class="p-3 text-center text-white-50 font-size-13">
            No countries found
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "SearchableCountrySelect",
  props: {
    value: {
      type: String,
      default: ''
    },
    countries: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select Country'
    },
    direction: {
      type: String,
      default: 'auto', // 'auto', 'up', 'down'
      validator: val => ['auto', 'up', 'down'].includes(val)
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      openUpwards: false,
      maxListHeight: 210
    };
  },
  computed: {
    selectedCountry() {
      if (!this.value || !this.countries) return null;
      return this.countries.find(c => c.code === this.value || c.name === this.value);
    },
    filteredCountries() {
      if (!this.searchQuery) return this.countries;
      const q = this.searchQuery.toLowerCase().trim();
      return this.countries.filter(c => 
        c.name && c.name.toLowerCase().includes(q)
      );
    },
    optionsListStyle() {
      return {
        maxHeight: `${this.maxListHeight}px`
      };
    }
  },
  methods: {
    determineDirection() {
      if (this.direction === 'up') {
        this.openUpwards = true;
        return;
      }
      if (this.direction === 'down') {
        this.openUpwards = false;
        return;
      }

      if (!this.$refs.selectContainer) return;
      const rect = this.$refs.selectContainer.getBoundingClientRect();
      const viewportHeight = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 800;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Estimated full dropdown height including search bar (~54px), list (~210px), borders/margins (~16px) = ~280px
      const estimatedDropdownHeight = 280;

      if (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow) {
        this.openUpwards = true;
        const availableSpace = spaceAbove - 70;
        this.maxListHeight = Math.max(120, Math.min(210, availableSpace));
      } else {
        this.openUpwards = false;
        const availableSpace = spaceBelow - 70;
        this.maxListHeight = Math.max(120, Math.min(210, availableSpace));
      }
    },
    toggleDropdown() {
      if (!this.isOpen) {
        this.determineDirection();
        this.isOpen = true;
        this.searchQuery = '';
        this.$nextTick(() => {
          this.determineDirection();
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      } else {
        this.isOpen = false;
      }
    },
    selectCountry(country) {
      this.$emit('input', country.code);
      this.$emit('change', country);
      this.isOpen = false;
      this.searchQuery = '';
    },
    handleClickOutside(event) {
      if (this.$refs.selectContainer && !this.$refs.selectContainer.contains(event.target)) {
        this.isOpen = false;
      }
    },
    handleWindowChange() {
      if (this.isOpen) {
        this.determineDirection();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.handleWindowChange, { passive: true });
    window.addEventListener('scroll', this.handleWindowChange, { passive: true });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleWindowChange);
    window.removeEventListener('scroll', this.handleWindowChange);
  }
};
</script>

<style scoped>
.searchable-country-select {
  width: 100%;
}

.border-pink-glow {
  border-color: #E53BB9 !important;
  box-shadow: 0 0 14px rgba(229, 59, 185, 0.35) !important;
}

.transition-transform {
  transition: transform 0.2s ease;
}

.country-dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 1050;
  background: #18181c;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  overflow: hidden;
}

.country-dropdown-menu.open-upwards {
  top: auto;
  bottom: calc(100% + 6px);
  box-shadow: 0 -12px 35px rgba(0, 0, 0, 0.6);
}

.search-input-wrapper {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 6px 12px;
  height: 38px;
  transition: all 0.2s ease;
}
.search-input-wrapper:focus-within {
  border-color: #E53BB9;
  box-shadow: 0 0 10px rgba(229, 59, 185, 0.35);
}

.country-search-input {
  background: transparent !important;
  border: none !important;
  outline: none !important;
  color: #ffffff !important;
  font-size: 13.5px !important;
  padding: 0 !important;
  height: 24px !important;
  width: 100% !important;
  box-shadow: none !important;
}
.country-search-input::placeholder {
  color: rgba(255, 255, 255, 0.4) !important;
}

.country-options-list {
  max-height: 210px;
  overflow-y: auto;
  padding: 4px 0;
}

.country-options-list::-webkit-scrollbar {
  width: 6px;
}
.country-options-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.country-option-item {
  padding: 9px 16px;
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: all 0.15s ease;
}
.country-option-item:hover {
  background: rgba(229, 59, 185, 0.15);
  color: #ffffff;
}
.country-option-item.active {
  background: rgba(229, 59, 185, 0.25);
  color: #ffffff;
  font-weight: 600;
}

.dropdown-fade-enter-active, .dropdown-fade-leave-active {
  transition: all 0.2s ease;
}
.dropdown-fade-enter, .dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.dropdown-fade-up-enter-active, .dropdown-fade-up-leave-active {
  transition: all 0.2s ease;
}
.dropdown-fade-up-enter, .dropdown-fade-up-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
